// Synthetic fixtures for the Books console mock data source (CLAUDE.md Law 2:
// no real bank data, statements, account numbers, balances, or transactions —
// everything below is synthetic and obviously so). Report shapes mirror
// contracts/fixtures/reports/*-2026-06.json byte-for-byte where a fixture
// exists (pnl, balance-sheet, by-location, py-comparison, cash-flow); GL,
// transactions, and balances are console-only mocks in the same spirit.

import {
  centsToDecimalString,
  parseDecimal,
} from "../../../financials-component/src/decimal.js";
import {
  MOOV_CARD_SUBJECT_PREFIX,
  type ChartSnapshot,
  type CheckNowStatus,
  type Company,
  type CompanyHealth,
  type DashboardGroup,
  type QueueItem,
} from "../types.js";
import type {
  ActivityItem,
  ActivityPage,
  ActivitySearchQuery,
  ActivitySearchResponse,
  ActivitySources,
  AdminMonthsResponse,
  BalanceHistory,
  BalancesSnapshot,
  BalanceSheetData,
  ByLocationData,
  CashFlowData,
  ConsoleTransaction,
  GeneralLedgerEntry,
  LedgerAccount,
  LedgerAccountsResponse,
  LedgerEntriesPage,
  LedgerEntry,
  LedgerHeader,
  LedgerNotPublishedReason,
  LedgerSearchQuery,
  LedgerSearchResponse,
  LedgerStatement,
  LedgerStatementReport,
  PnlData,
  PyComparisonData,
  ReportSnapshot,
  TrialBalance,
} from "./types.js";

export const DEFAULT_PERIOD = "2026-06";
export const DEFAULT_COMPANY = "gvpm";

const GENERATED_AT = "2026-07-01T06:00:00.000Z";
const PIPELINE_RUN_ID = "syn-run-reporting-0001";

function envelope<TData>(report: string, data: TData): ReportSnapshot<TData> {
  return {
    apiVersion: "v1",
    company: DEFAULT_COMPANY,
    report,
    period: DEFAULT_PERIOD,
    generatedAt: GENERATED_AT,
    pipelineRunId: PIPELINE_RUN_ID,
    data,
  };
}

export const SYNTHETIC_PNL: ReportSnapshot<PnlData> = envelope("pnl", {
  currency: "USD",
  periodStart: "2026-06-01",
  periodEnd: "2026-06-30",
  sections: [
    {
      key: "income",
      label: "Income",
      lines: [
        {
          account: "Income:GVPM:Services",
          label: "Management services",
          amount: "18500.00",
          priorYearAmount: null,
        },
      ],
      total: "18500.00",
    },
    {
      key: "expenses",
      label: "Expenses",
      lines: [
        {
          account: "Expenses:GVPM:Software",
          label: "Software",
          amount: "-642.17",
          priorYearAmount: null,
        },
        {
          account: "Expenses:GVPM:Rent",
          label: "Rent",
          amount: "-2500.00",
          priorYearAmount: null,
        },
        {
          account: "Expenses:GVPM:Professional",
          label: "Professional services",
          amount: "-1200.00",
          priorYearAmount: null,
        },
      ],
      total: "-4342.17",
    },
  ],
  netIncome: "14157.83",
  priorYearNetIncome: null,
});

export const SYNTHETIC_BALANCE_SHEET: ReportSnapshot<BalanceSheetData> = envelope(
  "balance-sheet",
  {
    currency: "USD",
    asOf: "2026-06-30",
    sections: [
      {
        key: "assets",
        label: "Assets",
        lines: [
          {
            account: "Assets:GVPM:Chase:Operating",
            label: "Operating checking",
            amount: "84213.55",
          },
          { account: "Assets:GVPM:Due-From:GVFO", label: "Due from GVFO", amount: "1000.00" },
        ],
        total: "85213.55",
      },
      {
        key: "liabilities",
        label: "Liabilities",
        lines: [
          { account: "Liabilities:GVPM:CreditCard", label: "Credit card", amount: "-1842.17" },
        ],
        total: "-1842.17",
      },
      {
        key: "equity",
        label: "Equity",
        lines: [
          {
            account: "Equity:GVPM:Opening-Balances",
            label: "Opening balances",
            amount: "-69213.55",
          },
          { account: "Equity:GVPM:Retained", label: "Retained earnings", amount: "-14157.83" },
        ],
        total: "-83371.38",
      },
    ],
  },
);

export const SYNTHETIC_CASH_FLOW: ReportSnapshot<CashFlowData> = envelope("cash-flow", {
  currency: "USD",
  periodStart: "2026-06-01",
  periodEnd: "2026-06-30",
  openingCash: "72555.72",
  closingCash: "84213.55",
  sections: [
    {
      key: "operating",
      label: "Operating",
      lines: [{ label: "Net receipts", amount: "14157.83" }],
      total: "14157.83",
    },
    {
      key: "financing",
      label: "Financing",
      lines: [{ label: "Loan principal", amount: "-2500.00" }],
      total: "-2500.00",
    },
  ],
  netChange: "11657.83",
});

export const SYNTHETIC_BY_LOCATION: ReportSnapshot<ByLocationData> = envelope("by-location", {
  currency: "USD",
  locations: [
    { location: "hq", income: "18500.00", expenses: "-4342.17", net: "14157.83" },
    { location: "remote", income: "0.00", expenses: "0.00", net: "0.00" },
  ],
  parents: [],
});

export const SYNTHETIC_PY_COMPARISON: ReportSnapshot<PyComparisonData> = envelope(
  "py-comparison",
  {
    currency: "USD",
    priorPeriod: "2025-06",
    available: false,
    note: "Activates automatically once 13 months of data exist.",
    lines: [],
  },
);

// --- General ledger ---------------------------------------------------------

/**
 * Synthetic GL rows for 2026-06.
 * `runningBalance` is the **company** (snapshot) quantity — the unfiltered
 * cumulative at each row. The reader recomputes filtered-mode totals at read
 * time from `amount`; these strings are not the filtered quantity.
 */
export const SYNTHETIC_GL_ENTRIES: GeneralLedgerEntry[] = [
  {
    date: "2026-06-01",
    account: "Assets:GVPM:Chase:Operating",
    payee: "SYNTHETIC OPENING",
    narration: "Opening balance carried forward",
    amount: "72555.72",
    runningBalance: "72555.72",
    location: "hq",
    source: "opening",
  },
  {
    date: "2026-06-03",
    account: "Income:GVPM:Services",
    payee: "SYNTHETIC CLIENT ONE",
    narration: "Management services — June retainer",
    amount: "18500.00",
    runningBalance: "91055.72",
    location: "hq",
    source: "plaid",
  },
  {
    date: "2026-06-05",
    account: "Expenses:GVPM:Rent",
    payee: "SYNTHETIC LANDLORD LLC",
    narration: "Office rent",
    amount: "-2500.00",
    runningBalance: "88555.72",
    location: "hq",
    source: "rule",
  },
  {
    date: "2026-06-10",
    account: "Expenses:GVPM:Software",
    payee: "SYNTHETIC SAAS VENDOR",
    narration: "Practice management software",
    amount: "-642.17",
    runningBalance: "87913.55",
    location: "hq",
    source: "rule",
  },
  {
    date: "2026-06-18",
    account: "Expenses:GVPM:Professional",
    payee: "SYNTHETIC CPA GROUP",
    narration: "Bookkeeping review",
    amount: "-1200.00",
    runningBalance: "86713.55",
    location: "hq",
    source: "ai-categorizer",
  },
  {
    date: "2026-06-24",
    account: "Liabilities:GVPM:CreditCard",
    payee: "SYNTHETIC CARD PAYMENT",
    narration: "Credit card payment",
    amount: "-2500.00",
    runningBalance: "84213.55",
    location: "remote",
    source: "plaid",
  },
];

// --- Transactions (posted + queued; distinct from the review Queue) --------

export const SYNTHETIC_TRANSACTIONS: ConsoleTransaction[] = [
  {
    id: "txn_syn_1",
    company: DEFAULT_COMPANY,
    date: "2026-06-03",
    amount: "18500.00",
    merchant: "SYNTHETIC CLIENT ONE",
    descriptor: "ACH CREDIT SYNTHETIC CLIENT ONE",
    account: "Income:GVPM:Services",
    location: "hq",
    status: "posted",
    plaidTransactionId: "plaid_syn_1",
    source: "plaid",
  },
  {
    id: "txn_syn_2",
    company: DEFAULT_COMPANY,
    date: "2026-06-05",
    amount: "-2500.00",
    merchant: "SYNTHETIC LANDLORD LLC",
    descriptor: "ACH DEBIT SYNTHETIC LANDLORD",
    account: "Expenses:GVPM:Rent",
    location: "hq",
    status: "posted",
    plaidTransactionId: "plaid_syn_2",
    source: "rule",
  },
  {
    id: "txn_syn_3",
    company: DEFAULT_COMPANY,
    date: "2026-06-10",
    amount: "-642.17",
    merchant: "SYNTHETIC SAAS VENDOR",
    descriptor: "CARD PURCHASE SYNTHETIC SAAS",
    account: "Expenses:GVPM:Software",
    location: "hq",
    status: "posted",
    plaidTransactionId: "plaid_syn_3",
    source: "rule",
  },
  {
    id: "txn_syn_4",
    company: DEFAULT_COMPANY,
    date: "2026-06-27",
    amount: "-186.40",
    merchant: "SYNTHETIC NEW VENDOR CO",
    descriptor: "CARD PURCHASE SYNTHETIC NEW VENDOR",
    status: "queued",
    plaidTransactionId: "plaid_syn_4",
    source: "plaid",
  },
  {
    id: "txn_syn_5",
    company: DEFAULT_COMPANY,
    date: "2026-06-29",
    amount: "-58.00",
    merchant: "SYNTHETIC RIDESHARE",
    descriptor: "CARD PURCHASE SYNTHETIC RIDESHARE",
    status: "queued",
    plaidTransactionId: "plaid_syn_5",
    source: "plaid",
  },
];

// --- Balances (Seam C) -------------------------------------------------------

export const SYNTHETIC_BALANCES: BalancesSnapshot = {
  company: DEFAULT_COMPANY,
  asOf: "2026-06-30",
  accounts: [
    {
      name: "Operating checking",
      type: "depository",
      mask: "4821",
      currency: "USD",
      currentBalance: "84213.55",
      availableBalance: "84213.55",
      asOf: "2026-06-30",
    },
    {
      name: "Business credit card",
      type: "credit",
      mask: "9034",
      currency: "USD",
      currentBalance: "-1842.17",
      creditLimit: "15000.00",
      asOf: "2026-06-30",
    },
  ],
};

export const SYNTHETIC_BALANCE_HISTORY: Record<string, BalanceHistory> = {
  "Operating checking": {
    company: DEFAULT_COMPANY,
    accountName: "Operating checking",
    points: [
      { asOf: "2026-06-24", currentBalance: "86713.55", availableBalance: "86713.55" },
      { asOf: "2026-06-25", currentBalance: "86713.55", availableBalance: "86713.55" },
      { asOf: "2026-06-26", currentBalance: "86713.55", availableBalance: "86713.55" },
      { asOf: "2026-06-27", currentBalance: "84213.55", availableBalance: "84213.55" },
      { asOf: "2026-06-28", currentBalance: "84213.55", availableBalance: "84213.55" },
      { asOf: "2026-06-29", currentBalance: "84213.55", availableBalance: "84213.55" },
      { asOf: "2026-06-30", currentBalance: "84213.55", availableBalance: "84213.55" },
    ],
  },
  "Business credit card": {
    company: DEFAULT_COMPANY,
    accountName: "Business credit card",
    points: [
      { asOf: "2026-06-24", currentBalance: "-4342.17", creditLimit: "15000.00" },
      { asOf: "2026-06-25", currentBalance: "-4342.17", creditLimit: "15000.00" },
      { asOf: "2026-06-26", currentBalance: "-4342.17", creditLimit: "15000.00" },
      { asOf: "2026-06-27", currentBalance: "-1842.17", creditLimit: "15000.00" },
      { asOf: "2026-06-28", currentBalance: "-1842.17", creditLimit: "15000.00" },
      { asOf: "2026-06-29", currentBalance: "-1842.17", creditLimit: "15000.00" },
      { asOf: "2026-06-30", currentBalance: "-1842.17", creditLimit: "15000.00" },
    ],
  },
};

// --- Redesign read models (activity, ledger view, demo shell) --------------
// Amounts are decimal strings. Every sum below goes through exact cents.

function money(cents: bigint): string {
  return centsToDecimalString(cents);
}

const RENT_CENTS = 50000n;
const LATE_FEE_CENTS = 5000n;
const MOOV_FEE_CENTS = 40n;
const TENANT_GROSS_CENTS = RENT_CENTS + LATE_FEE_CENTS;
const TENANT_NET_CENTS = TENANT_GROSS_CENTS - MOOV_FEE_CENTS;
const DEPOSIT_CENTS = 60000n;
const CLOSED_RENT_CENTS = 10000n;
const OPENING_CASH_CENTS = 100000n;

export const RENTAL_COMPANY_SLUG = "syn-rentals";
export const TRUST_COMPANY_SLUG = "syn-trust";
/** Practice-shaped slug: ledger view is not enabled. Not a special-case in the picker. */
export const LEDGER_VIEW_OFF_SLUG = "syn-view-off";

export const LEDGER_OPEN_PERIOD = "2026-10";
export const LEDGER_CLOSED_PERIOD = "2026-09";
export const LEDGER_NOT_PUBLISHED_PERIOD = "2026-08";
export const STATEMENTS_UNAVAILABLE_PERIOD = "2026-11";

const LEDGER_AS_OF = "2026-10-25T12:00:00.000Z";
const LEDGER_COMMIT_AT = "2026-10-25T11:06:00.000Z";
const PROPERTY = "123 Test Street";
const TENANT_LEASE = "TEST-26-0001";
const MOOV_PRINCIPAL = "0000000000000002";
const MOOV_FEE_ID = "0000000000000003";
const MOOV_PAIR = `${MOOV_PRINCIPAL} ${MOOV_FEE_ID}`;

export const TENANT_PAYMENT_AMOUNTS = {
  rent: money(RENT_CENTS),
  lateFee: money(LATE_FEE_CENTS),
  fee: money(MOOV_FEE_CENTS),
  gross: money(TENANT_GROSS_CENTS),
  net: money(TENANT_NET_CENTS),
} as const;

const ACTIVITY_SOURCES_LIVE: ActivitySources = {
  bank: { lastSyncedAt: "2026-10-25T11:00:00.000Z" },
  moov: { walletConfigured: true, lastReadAt: "2026-10-25T11:02:00.000Z" },
  ledger: { available: true, asOf: LEDGER_COMMIT_AT },
};

const rentalActivityItems: ActivityItem[] = [
  {
    id: "act-syn-0001",
    source: "bank",
    date: "2026-10-02",
    amount: money(10000n),
    description: "ACH CREDIT SYNTHETIC CLIENT ONE",
    state: "processed",
    refId: "0000000000000001",
  },
  {
    id: "act-syn-0002",
    source: "moov",
    date: "2026-10-16",
    amount: money(TENANT_GROSS_CENTS),
    fee: money(MOOV_FEE_CENTS),
    net: money(TENANT_NET_CENTS),
    description: "Tenant payment",
    state: "booked",
    refId: MOOV_PRINCIPAL,
    moov: { kind: "tenant_payment", property: PROPERTY, lease: TENANT_LEASE },
    booking: {
      by: "system_match",
      accounts: ["Income:Rent", "Income:LateFee", "Expenses:ProcessorFee"],
    },
  },
  {
    id: "act-syn-0003",
    source: "moov",
    date: "2026-10-18",
    amount: money(20000n),
    fee: money(MOOV_FEE_CENTS),
    net: money(20000n - MOOV_FEE_CENTS),
    description: "Tenant payment needs a category",
    state: "needs_you",
    refId: "0000000000000004",
    moov: { kind: "tenant_payment", property: PROPERTY, lease: "TEST-26-0002" },
  },
  {
    id: "act-syn-0004",
    source: "moov",
    date: "2026-10-20",
    amount: money(7500n),
    fee: money(MOOV_FEE_CENTS),
    net: money(7500n - MOOV_FEE_CENTS),
    description: "Dismissed wallet payment",
    state: "dismissed",
    refId: "0000000000000005",
    moov: { kind: "tenant_payment", property: PROPERTY, lease: "TEST-26-0003" },
  },
  {
    id: "act-syn-0007",
    source: "bank",
    date: "2026-10-03",
    amount: money(-4200n),
    description: "SYNTHETIC BOOKED BY RECEIPT",
    state: "processed",
    refId: "0000000000000007",
    // rd15 A1: the bank account the line came from, name and last four only.
    account: { id: "0000000000000acct1", name: "Synthetic Checking", mask: "0001" },
    booking: { by: "receipt", accounts: ["Expenses:Office"], location: PROPERTY },
  },
  {
    id: "act-syn-0008",
    source: "bank",
    date: "2026-10-04",
    amount: money(-2900n),
    description: "SYNTHETIC BOOKED BY RULE",
    state: "processed",
    refId: "0000000000000008",
    account: { id: "0000000000000acct1", name: "Synthetic Checking", mask: "0001" },
    // rd15 A2: the number in the rule's own source.
    booking: { by: "rule", ruleId: 14, accounts: ["Expenses:Software"] },
  },
  {
    id: "act-syn-0009",
    source: "bank",
    date: "2026-10-05",
    amount: money(-810n),
    description: "SYNTHETIC BOOKED BY AI",
    state: "processed",
    refId: "0000000000000009",
    booking: { by: "ai", accounts: ["Expenses:Software:Hosting"] },
  },
  {
    id: "act-syn-0010",
    source: "bank",
    date: "2026-10-06",
    amount: money(-1900n),
    description: "SYNTHETIC BOOKED BY PERSON",
    state: "processed",
    refId: "0000000000000010",
    account: { id: "0000000000000acct2", name: "Synthetic Savings", mask: "0002" },
    // rd15 A2: a staff login is the only name there is; there is no display name.
    booking: {
      by: "person",
      person: "synthetic-owner@example.test",
      accounts: ["Expenses:Repairs"],
      location: PROPERTY,
    },
  },
  {
    id: "act-syn-0011",
    source: "bank",
    date: "2026-10-07",
    amount: money(-6000n),
    description: "SYNTHETIC BOOKED WITH NO SOURCE CLUE",
    state: "processed",
    refId: "0000000000000011",
    booking: { accounts: ["Expenses:Prior"] },
  },
];

const trustActivityItems: ActivityItem[] = [
  {
    id: "act-syn-0006",
    source: "moov",
    date: "2026-10-12",
    amount: money(DEPOSIT_CENTS),
    description: "Security deposit",
    state: "booked",
    refId: "0000000000000006",
    moov: { kind: "security_deposit", property: PROPERTY },
  },
];

export const SYNTHETIC_ACTIVITY: ActivityPage = {
  items: rentalActivityItems,
  page: 1,
  pageSize: 50,
  total: rentalActivityItems.length,
  hasMore: false,
  sources: ACTIVITY_SOURCES_LIVE,
};

export const SYNTHETIC_TRUST_ACTIVITY: ActivityPage = {
  items: trustActivityItems,
  page: 1,
  pageSize: 50,
  total: trustActivityItems.length,
  hasMore: false,
  sources: ACTIVITY_SOURCES_LIVE,
};

function activityForCompany(company: string): ActivityPage {
  if (company === TRUST_COMPANY_SLUG) return SYNTHETIC_TRUST_ACTIVITY;
  if (company === RENTAL_COMPANY_SLUG) return SYNTHETIC_ACTIVITY;
  return {
    items: [],
    page: 1,
    pageSize: 50,
    total: 0,
    hasMore: false,
    sources: {
      bank: {},
      moov: { walletConfigured: false },
      ledger: { available: false },
    },
  };
}

const rentalClosedEntry: LedgerEntry = {
  id: "syn-rentals-2026-09-1",
  date: "2026-09-05",
  flag: "*",
  payee: "SYNTHETIC LANDLORD LLC",
  narration: "Office rent",
  source: "rule",
  location: PROPERTY,
  ids: {},
  postings: [
    { account: "Expenses:Rent", amount: money(CLOSED_RENT_CENTS), location: PROPERTY },
    { account: "Assets:Wallet:Operating", amount: money(-CLOSED_RENT_CENTS), location: PROPERTY },
  ],
  view: {
    kind: "expense",
    headline: "Office rent",
    subline: PROPERTY,
    cashAccount: "Assets:Wallet:Operating",
    cashDelta: money(-CLOSED_RENT_CENTS),
    lines: [{ label: "Office rent", amount: money(-CLOSED_RENT_CENTS) }],
  },
};

const rentalOpenEntry: LedgerEntry = {
  id: "syn-rentals-2026-10-1",
  date: "2026-10-16",
  flag: "*",
  narration: "Tenant payment",
  source: "moov",
  location: PROPERTY,
  ids: { moovId: MOOV_PAIR, lease: TENANT_LEASE },
  postings: [
    { account: "Assets:Wallet:Operating", amount: money(TENANT_NET_CENTS), location: PROPERTY },
    { account: "Expenses:ProcessorFee", amount: money(MOOV_FEE_CENTS), location: PROPERTY },
    { account: "Income:Rent", amount: money(-RENT_CENTS), location: PROPERTY },
    { account: "Income:LateFee", amount: money(-LATE_FEE_CENTS), location: PROPERTY },
  ],
  view: {
    kind: "tenant_payment",
    headline: "Tenant payment",
    subline: `${PROPERTY} · ${TENANT_LEASE}`,
    cashAccount: "Assets:Wallet:Operating",
    cashDelta: money(TENANT_NET_CENTS),
    lines: [
      { label: "Rent", amount: money(RENT_CENTS) },
      { label: "Late fee", amount: money(LATE_FEE_CENTS) },
      { label: "Moov fee", amount: money(-MOOV_FEE_CENTS) },
    ],
  },
};

const trustDepositEntry: LedgerEntry = {
  id: "syn-trust-2026-10-1",
  date: "2026-10-12",
  flag: "*",
  narration: "Security deposit",
  source: "moov",
  location: PROPERTY,
  ids: { moovId: "0000000000000006" },
  postings: [
    { account: "Assets:Trust:Cash", amount: money(DEPOSIT_CENTS), location: PROPERTY },
    {
      account: "Liabilities:SecurityDeposits",
      amount: money(-DEPOSIT_CENTS),
      location: PROPERTY,
    },
  ],
  view: {
    kind: "deposit",
    headline: "Security deposit",
    subline: PROPERTY,
    cashAccount: "Assets:Trust:Cash",
    cashDelta: money(DEPOSIT_CENTS),
    lines: [{ label: "Security deposit", amount: money(DEPOSIT_CENTS) }],
  },
};

function accountRow(
  account: string,
  type: string,
  isCash: boolean,
  opening: bigint,
  activity: bigint,
  openedOn?: string,
): LedgerAccount {
  const row: LedgerAccount = {
    account,
    type,
    opening: money(opening),
    activity: money(activity),
    closing: money(opening + activity),
    isCash,
  };
  if (openedOn !== undefined) row.openedOn = openedOn;
  return row;
}

const rentalClosedAccounts: LedgerAccount[] = [
  accountRow(
    "Assets:Wallet:Operating",
    "Assets",
    true,
    OPENING_CASH_CENTS,
    -CLOSED_RENT_CENTS,
    "2026-01-01",
  ),
  accountRow("Expenses:Rent", "Expenses", false, 0n, CLOSED_RENT_CENTS, "2026-01-01"),
  accountRow("Equity:Opening", "Equity", false, -OPENING_CASH_CENTS, 0n, "2026-01-01"),
];

const walletAfterSeptember = OPENING_CASH_CENTS - CLOSED_RENT_CENTS;
const rentalOpenAccounts: LedgerAccount[] = [
  accountRow(
    "Assets:Wallet:Operating",
    "Assets",
    true,
    walletAfterSeptember,
    TENANT_NET_CENTS,
    "2026-01-01",
  ),
  accountRow("Expenses:Rent", "Expenses", false, CLOSED_RENT_CENTS, 0n, "2026-01-01"),
  accountRow("Expenses:ProcessorFee", "Expenses", false, 0n, MOOV_FEE_CENTS, "2026-01-01"),
  accountRow("Income:Rent", "Income", false, 0n, -RENT_CENTS, "2026-01-01"),
  accountRow("Income:LateFee", "Income", false, 0n, -LATE_FEE_CENTS, "2026-01-01"),
  accountRow("Equity:Opening", "Equity", false, -OPENING_CASH_CENTS, 0n, "2026-01-01"),
];

const rentalNovemberAccounts: LedgerAccount[] = rentalOpenAccounts.map((row) =>
  accountRow(
    row.account,
    row.type,
    row.isCash,
    parseDecimal(row.closing),
    0n,
    row.openedOn,
  ),
);

const trustAccounts: LedgerAccount[] = [
  accountRow("Assets:Trust:Cash", "Assets", true, 0n, DEPOSIT_CENTS, "2026-01-01"),
  accountRow(
    "Liabilities:SecurityDeposits",
    "Liabilities",
    false,
    0n,
    -DEPOSIT_CENTS,
    "2026-01-01",
  ),
];

function trialBalanceOf(accounts: readonly LedgerAccount[]): TrialBalance {
  let debits = 0n;
  let credits = 0n;
  for (const account of accounts) {
    const closing = parseDecimal(account.closing);
    if (closing >= 0n) debits += closing;
    else credits -= closing;
  }
  return { totalDebits: money(debits), totalCredits: money(credits) };
}

function assertBooks(accounts: readonly LedgerAccount[], entries: readonly LedgerEntry[]): void {
  for (const account of accounts) {
    const rolled = parseDecimal(account.opening) + parseDecimal(account.activity);
    if (money(rolled) !== account.closing) {
      throw new Error(`synthetic roll-forward mismatch for ${account.account}`);
    }
  }
  const trial = trialBalanceOf(accounts);
  if (trial.totalDebits !== trial.totalCredits) {
    throw new Error(
      `synthetic trial balance ${trial.totalDebits} vs ${trial.totalCredits}`,
    );
  }
  for (const entry of entries) {
    let sum = 0n;
    for (const posting of entry.postings) sum += parseDecimal(posting.amount);
    if (sum !== 0n) throw new Error(`synthetic entry does not balance: ${entry.id}`);
  }
}

assertBooks(rentalClosedAccounts, [rentalClosedEntry]);
assertBooks(rentalOpenAccounts, [rentalOpenEntry]);
assertBooks(rentalNovemberAccounts, []);
assertBooks(trustAccounts, [trustDepositEntry]);

const RENTAL_PENDING = 16;
const TRUST_PENDING = 15;

function headerFor(
  company: string,
  period: string,
  state: "open" | "closed",
  waitingForReview: number,
  periods: LedgerHeader["periods"],
): LedgerHeader {
  return {
    company,
    period,
    state,
    asOf: state === "closed" ? "2026-09-30T23:59:00.000Z" : LEDGER_AS_OF,
    ledgerCommitAt: LEDGER_COMMIT_AT,
    loaderErrors: 0,
    waitingForReview,
    periods,
  };
}

const rentalPeriods: LedgerHeader["periods"] = [
  { period: LEDGER_CLOSED_PERIOD, state: "closed", entryCount: 1 },
  { period: LEDGER_OPEN_PERIOD, state: "open", entryCount: 1 },
  { period: STATEMENTS_UNAVAILABLE_PERIOD, state: "open", entryCount: 0 },
];

const trustPeriods: LedgerHeader["periods"] = [
  { period: LEDGER_OPEN_PERIOD, state: "open", entryCount: 1 },
];

function pageFor(
  header: LedgerHeader,
  entries: LedgerEntry[],
): LedgerEntriesPage {
  return {
    header,
    entries,
    page: 1,
    pageSize: 50,
    total: entries.length,
    hasMore: false,
  };
}

export const SYNTHETIC_RENTAL_LEDGER_CLOSED: LedgerEntriesPage = pageFor(
  headerFor(RENTAL_COMPANY_SLUG, LEDGER_CLOSED_PERIOD, "closed", 0, rentalPeriods),
  [rentalClosedEntry],
);

export const SYNTHETIC_RENTAL_LEDGER: LedgerEntriesPage = pageFor(
  headerFor(RENTAL_COMPANY_SLUG, LEDGER_OPEN_PERIOD, "open", RENTAL_PENDING, rentalPeriods),
  [rentalOpenEntry],
);

export const SYNTHETIC_RENTAL_LEDGER_UNSTATED: LedgerEntriesPage = pageFor(
  headerFor(RENTAL_COMPANY_SLUG, STATEMENTS_UNAVAILABLE_PERIOD, "open", 0, rentalPeriods),
  [],
);

export const SYNTHETIC_TRUST_LEDGER: LedgerEntriesPage = pageFor(
  headerFor(TRUST_COMPANY_SLUG, LEDGER_OPEN_PERIOD, "open", TRUST_PENDING, trustPeriods),
  [trustDepositEntry],
);

function accountsResponse(
  header: LedgerHeader,
  accounts: LedgerAccount[],
): LedgerAccountsResponse {
  return { header, accounts, trialBalance: trialBalanceOf(accounts) };
}

export const SYNTHETIC_RENTAL_ACCOUNTS_CLOSED = accountsResponse(
  SYNTHETIC_RENTAL_LEDGER_CLOSED.header,
  rentalClosedAccounts,
);
export const SYNTHETIC_RENTAL_ACCOUNTS = accountsResponse(
  SYNTHETIC_RENTAL_LEDGER.header,
  rentalOpenAccounts,
);
export const SYNTHETIC_RENTAL_ACCOUNTS_UNSTATED = accountsResponse(
  SYNTHETIC_RENTAL_LEDGER_UNSTATED.header,
  rentalNovemberAccounts,
);
export const SYNTHETIC_TRUST_ACCOUNTS = accountsResponse(
  SYNTHETIC_TRUST_LEDGER.header,
  trustAccounts,
);

const PROVISIONAL_NOTICE = "This month is not closed or reconciled.";

function statementEnvelope<TData>(
  company: string,
  report: LedgerStatementReport,
  period: string,
  state: "open" | "closed",
  throughDate: string,
  data: TData,
): LedgerStatement<TData> {
  return {
    apiVersion: "v1",
    company,
    report,
    period,
    generatedAt: LEDGER_AS_OF,
    pipelineRunId: "syn-run-0001",
    data,
    state,
    asOf: state === "closed" ? "2026-09-30T23:59:00.000Z" : LEDGER_AS_OF,
    throughDate,
  };
}

const openPnl: PnlData = {
  currency: "USD",
  periodStart: "2026-10-01",
  periodEnd: "2026-10-25",
  sections: [
    {
      key: "income",
      label: "Income",
      lines: [
        {
          account: "Income:Rent",
          label: "Rent",
          amount: money(RENT_CENTS),
          priorYearAmount: null,
        },
        {
          account: "Income:LateFee",
          label: "Late fee",
          amount: money(LATE_FEE_CENTS),
          priorYearAmount: null,
        },
      ],
      total: money(TENANT_GROSS_CENTS),
    },
    {
      key: "expenses",
      label: "Expenses",
      lines: [
        {
          account: "Expenses:ProcessorFee",
          label: "Moov fee",
          amount: money(-MOOV_FEE_CENTS),
          priorYearAmount: null,
        },
      ],
      total: money(-MOOV_FEE_CENTS),
    },
  ],
  netIncome: money(TENANT_NET_CENTS),
  priorYearNetIncome: null,
};

const openBalanceSheet: BalanceSheetData = {
  currency: "USD",
  asOf: "2026-10-25",
  provisionalNotice: PROVISIONAL_NOTICE,
  sections: [
    {
      key: "assets",
      label: "Assets",
      lines: [
        {
          account: "Assets:Wallet:Operating",
          label: "Operating wallet",
          amount: money(walletAfterSeptember + TENANT_NET_CENTS),
        },
      ],
      total: money(walletAfterSeptember + TENANT_NET_CENTS),
    },
    {
      key: "equity",
      label: "Equity",
      lines: [
        {
          account: "Equity:Opening",
          label: "Opening balances",
          amount: money(-OPENING_CASH_CENTS),
        },
        {
          account: "Equity:Unclosed",
          label: "Unclosed income and expense",
          amount: money(-(walletAfterSeptember + TENANT_NET_CENTS - OPENING_CASH_CENTS)),
        },
      ],
      total: money(-(walletAfterSeptember + TENANT_NET_CENTS)),
    },
  ],
};

const openCashFlow: CashFlowData = {
  currency: "USD",
  periodStart: "2026-10-01",
  periodEnd: "2026-10-25",
  openingCash: money(walletAfterSeptember),
  closingCash: money(walletAfterSeptember + TENANT_NET_CENTS),
  sections: [
    {
      key: "operating",
      label: "Operating",
      lines: [{ label: "Tenant payment net of fee", amount: money(TENANT_NET_CENTS) }],
      total: money(TENANT_NET_CENTS),
    },
  ],
  netChange: money(TENANT_NET_CENTS),
};

const openByLocation: ByLocationData = {
  currency: "USD",
  locations: [
    {
      location: PROPERTY,
      income: money(TENANT_GROSS_CENTS),
      expenses: money(-MOOV_FEE_CENTS),
      net: money(TENANT_NET_CENTS),
    },
  ],
};

export const SYNTHETIC_PROVISIONAL_STATEMENTS = {
  pnl: statementEnvelope(
    RENTAL_COMPANY_SLUG,
    "pnl",
    LEDGER_OPEN_PERIOD,
    "open",
    "2026-10-25",
    openPnl,
  ),
  "balance-sheet": statementEnvelope(
    RENTAL_COMPANY_SLUG,
    "balance-sheet",
    LEDGER_OPEN_PERIOD,
    "open",
    "2026-10-25",
    openBalanceSheet,
  ),
  "cash-flow": statementEnvelope(
    RENTAL_COMPANY_SLUG,
    "cash-flow",
    LEDGER_OPEN_PERIOD,
    "open",
    "2026-10-25",
    openCashFlow,
  ),
  "by-location": statementEnvelope(
    RENTAL_COMPANY_SLUG,
    "by-location",
    LEDGER_OPEN_PERIOD,
    "open",
    "2026-10-25",
    openByLocation,
  ),
};

const closedPnl: PnlData = {
  currency: "USD",
  periodStart: "2026-09-01",
  periodEnd: "2026-09-30",
  sections: [
    {
      key: "income",
      label: "Income",
      lines: [],
      total: "0.00",
    },
    {
      key: "expenses",
      label: "Expenses",
      lines: [
        {
          account: "Expenses:Rent",
          label: "Office rent",
          amount: money(-CLOSED_RENT_CENTS),
          priorYearAmount: null,
        },
      ],
      total: money(-CLOSED_RENT_CENTS),
    },
  ],
  netIncome: money(-CLOSED_RENT_CENTS),
  priorYearNetIncome: null,
};

const closedBalanceSheet: BalanceSheetData = {
  currency: "USD",
  asOf: "2026-09-30",
  sections: [
    {
      key: "assets",
      label: "Assets",
      lines: [
        {
          account: "Assets:Wallet:Operating",
          label: "Operating wallet",
          amount: money(walletAfterSeptember),
        },
      ],
      total: money(walletAfterSeptember),
    },
    {
      key: "equity",
      label: "Equity",
      lines: [
        {
          account: "Equity:Opening",
          label: "Opening balances",
          amount: money(-OPENING_CASH_CENTS),
        },
        {
          account: "Expenses:Rent",
          label: "Office rent",
          amount: money(CLOSED_RENT_CENTS),
        },
      ],
      total: money(-walletAfterSeptember),
    },
  ],
};

export const SYNTHETIC_CLOSED_STATEMENTS = {
  pnl: statementEnvelope(
    RENTAL_COMPANY_SLUG,
    "pnl",
    LEDGER_CLOSED_PERIOD,
    "closed",
    "2026-09-30",
    closedPnl,
  ),
  "balance-sheet": statementEnvelope(
    RENTAL_COMPANY_SLUG,
    "balance-sheet",
    LEDGER_CLOSED_PERIOD,
    "closed",
    "2026-09-30",
    closedBalanceSheet,
  ),
  "cash-flow": statementEnvelope(
    RENTAL_COMPANY_SLUG,
    "cash-flow",
    LEDGER_CLOSED_PERIOD,
    "closed",
    "2026-09-30",
    {
      currency: "USD",
      periodStart: "2026-09-01",
      periodEnd: "2026-09-30",
      openingCash: money(OPENING_CASH_CENTS),
      closingCash: money(walletAfterSeptember),
      sections: [
        {
          key: "operating",
          label: "Operating",
          lines: [{ label: "Office rent", amount: money(-CLOSED_RENT_CENTS) }],
          total: money(-CLOSED_RENT_CENTS),
        },
      ],
      netChange: money(-CLOSED_RENT_CENTS),
    } satisfies CashFlowData,
  ),
  "by-location": statementEnvelope(
    RENTAL_COMPANY_SLUG,
    "by-location",
    LEDGER_CLOSED_PERIOD,
    "closed",
    "2026-09-30",
    {
      currency: "USD",
      locations: [
        {
          location: PROPERTY,
          income: "0.00",
          expenses: money(-CLOSED_RENT_CENTS),
          net: money(-CLOSED_RENT_CENTS),
        },
      ],
    } satisfies ByLocationData,
  ),
};

const trustBalanceSheet: BalanceSheetData = {
  currency: "USD",
  asOf: "2026-10-25",
  provisionalNotice: PROVISIONAL_NOTICE,
  sections: [
    {
      key: "assets",
      label: "Assets",
      lines: [
        {
          account: "Assets:Trust:Cash",
          label: "Trust cash",
          amount: money(DEPOSIT_CENTS),
        },
      ],
      total: money(DEPOSIT_CENTS),
    },
    {
      key: "liabilities",
      label: "Liabilities",
      lines: [
        {
          account: "Liabilities:SecurityDeposits",
          label: "Security deposits",
          amount: money(-DEPOSIT_CENTS),
        },
      ],
      total: money(-DEPOSIT_CENTS),
    },
  ],
};

export const SYNTHETIC_TRUST_STATEMENTS = {
  pnl: statementEnvelope(TRUST_COMPANY_SLUG, "pnl", LEDGER_OPEN_PERIOD, "open", "2026-10-25", {
    currency: "USD",
    periodStart: "2026-10-01",
    periodEnd: "2026-10-25",
    sections: [],
    netIncome: "0.00",
    priorYearNetIncome: null,
  } satisfies PnlData),
  "balance-sheet": statementEnvelope(
    TRUST_COMPANY_SLUG,
    "balance-sheet",
    LEDGER_OPEN_PERIOD,
    "open",
    "2026-10-25",
    trustBalanceSheet,
  ),
  "cash-flow": statementEnvelope(
    TRUST_COMPANY_SLUG,
    "cash-flow",
    LEDGER_OPEN_PERIOD,
    "open",
    "2026-10-25",
    {
      currency: "USD",
      periodStart: "2026-10-01",
      periodEnd: "2026-10-25",
      openingCash: "0.00",
      closingCash: money(DEPOSIT_CENTS),
      sections: [
        {
          key: "financing",
          label: "Financing",
          lines: [{ label: "Security deposit", amount: money(DEPOSIT_CENTS) }],
          total: money(DEPOSIT_CENTS),
        },
      ],
      netChange: money(DEPOSIT_CENTS),
    } satisfies CashFlowData,
  ),
  "by-location": statementEnvelope(
    TRUST_COMPANY_SLUG,
    "by-location",
    LEDGER_OPEN_PERIOD,
    "open",
    "2026-10-25",
    {
      currency: "USD",
      locations: [{ location: PROPERTY, income: "0.00", expenses: "0.00", net: "0.00" }],
    } satisfies ByLocationData,
  ),
};

export function ledgerReadRefusal(
  company: string,
  period: string,
  surface: "entries" | "accounts" | "statement",
): LedgerNotPublishedReason | null {
  if (company === LEDGER_VIEW_OFF_SLUG) return "ledger_view_not_enabled";
  if (company === RENTAL_COMPANY_SLUG && period === LEDGER_NOT_PUBLISHED_PERIOD) {
    return "ledger_not_published";
  }
  if (company === TRUST_COMPANY_SLUG && period !== LEDGER_OPEN_PERIOD) {
    return "ledger_not_published";
  }
  if (
    company === RENTAL_COMPANY_SLUG &&
    period === STATEMENTS_UNAVAILABLE_PERIOD &&
    surface === "statement"
  ) {
    return "statements_unavailable";
  }
  if (
    company !== RENTAL_COMPANY_SLUG &&
    company !== TRUST_COMPANY_SLUG &&
    company !== LEDGER_VIEW_OFF_SLUG
  ) {
    return null;
  }
  if (
    company === RENTAL_COMPANY_SLUG &&
    period !== LEDGER_OPEN_PERIOD &&
    period !== LEDGER_CLOSED_PERIOD &&
    period !== STATEMENTS_UNAVAILABLE_PERIOD &&
    period !== LEDGER_NOT_PUBLISHED_PERIOD
  ) {
    return "ledger_not_published";
  }
  return null;
}

export function notPublishedErrorBody(reason: LedgerNotPublishedReason): {
  error: {
    code: "not_found";
    message: "not found";
    details: { reason: LedgerNotPublishedReason };
  };
} {
  return {
    error: {
      code: "not_found",
      message: "not found",
      details: { reason },
    },
  };
}

export function syntheticActivityPage(company: string, period: string): ActivityPage {
  if (period !== LEDGER_OPEN_PERIOD) {
    return {
      items: [],
      page: 1,
      pageSize: 50,
      total: 0,
      hasMore: false,
      sources: activityForCompany(company).sources,
    };
  }
  return activityForCompany(company);
}

export function emptyLedgerPage(company: string, period: string): LedgerEntriesPage {
  return pageFor(headerFor(company, period, "open", 0, []), []);
}

export function emptyLedgerAccounts(company: string, period: string): LedgerAccountsResponse {
  return accountsResponse(headerFor(company, period, "open", 0, []), []);
}

export function syntheticLedgerPage(company: string, period: string): LedgerEntriesPage {
  if (company === TRUST_COMPANY_SLUG && period === LEDGER_OPEN_PERIOD) {
    return SYNTHETIC_TRUST_LEDGER;
  }
  if (company === RENTAL_COMPANY_SLUG && period === LEDGER_CLOSED_PERIOD) {
    return SYNTHETIC_RENTAL_LEDGER_CLOSED;
  }
  if (company === RENTAL_COMPANY_SLUG && period === STATEMENTS_UNAVAILABLE_PERIOD) {
    return SYNTHETIC_RENTAL_LEDGER_UNSTATED;
  }
  if (company === RENTAL_COMPANY_SLUG && period === LEDGER_OPEN_PERIOD) {
    return SYNTHETIC_RENTAL_LEDGER;
  }
  return emptyLedgerPage(company, period);
}

export function syntheticLedgerAccounts(
  company: string,
  period: string,
): LedgerAccountsResponse {
  if (company === TRUST_COMPANY_SLUG && period === LEDGER_OPEN_PERIOD) {
    return SYNTHETIC_TRUST_ACCOUNTS;
  }
  if (company === RENTAL_COMPANY_SLUG && period === LEDGER_CLOSED_PERIOD) {
    return SYNTHETIC_RENTAL_ACCOUNTS_CLOSED;
  }
  if (company === RENTAL_COMPANY_SLUG && period === STATEMENTS_UNAVAILABLE_PERIOD) {
    return SYNTHETIC_RENTAL_ACCOUNTS_UNSTATED;
  }
  if (company === RENTAL_COMPANY_SLUG && period === LEDGER_OPEN_PERIOD) {
    return SYNTHETIC_RENTAL_ACCOUNTS;
  }
  return emptyLedgerAccounts(company, period);
}

export function syntheticLedgerStatement(
  company: string,
  report: LedgerStatementReport,
  period: string,
): LedgerStatement {
  if (company === TRUST_COMPANY_SLUG && period === LEDGER_OPEN_PERIOD) {
    return SYNTHETIC_TRUST_STATEMENTS[report];
  }
  if (company === RENTAL_COMPANY_SLUG && period === LEDGER_CLOSED_PERIOD) {
    return SYNTHETIC_CLOSED_STATEMENTS[report];
  }
  if (company === RENTAL_COMPANY_SLUG && period === LEDGER_OPEN_PERIOD) {
    return SYNTHETIC_PROVISIONAL_STATEMENTS[report];
  }
  return statementEnvelope(company, report, period, "open", `${period}-01`, {
    currency: "USD",
    periodStart: `${period}-01`,
    periodEnd: `${period}-01`,
    sections: [],
    netIncome: "0.00",
    priorYearNetIncome: null,
  });
}

// --- G6 to G9 synthetic reads ----------------------------------------------

const SEARCH_FROM = "2025-09";
const SEARCH_TO = "2026-10";

const activitySearchItems: ActivityItem[] = [
  {
    id: "act-2001",
    source: "moov",
    date: "2026-10-04",
    amount: money(55000n),
    fee: money(40n),
    net: money(54960n),
    description: "SYNTHETIC TENANT PAYMENT",
    state: "booked",
    refId: "mov0000000000000001",
    moov: { kind: "payment", property: PROPERTY, lease: TENANT_LEASE },
    booking: {
      by: "system_match",
      accounts: ["Income:Rent", "Income:Late-Fee", "Expenses:Moov:Fee"],
    },
  },
  {
    id: "act-2002",
    source: "bank",
    date: "2026-04-12",
    amount: money(-4200n),
    description: "SYNTHETIC SOFTWARE",
    state: "processed",
    refId: "0000000000000001",
    booking: { by: "rule", accounts: ["Expenses:Software"] },
  },
  {
    id: "act-2003",
    source: "bank",
    date: "2025-11-20",
    amount: money(-1900n),
    description: "SYNTHETIC REPAIR SHOP",
    state: "processed",
    refId: "0000000000000002",
  },
];

const trustActivitySearchItems: ActivityItem[] = [
  {
    id: "act-syn-0006",
    source: "moov",
    date: "2026-10-12",
    amount: money(DEPOSIT_CENTS),
    description: "Security deposit",
    state: "booked",
    refId: "0000000000000006",
    moov: { kind: "security_deposit", property: PROPERTY },
    booking: { by: "system_match", accounts: ["Liabilities:SecurityDeposits"] },
  },
];

export const SYNTHETIC_ACTIVITY_SEARCH: ActivitySearchResponse = {
  items: activitySearchItems,
  matchCount: activitySearchItems.length,
  capped: false,
  searchedFrom: SEARCH_FROM,
  searchedTo: SEARCH_TO,
  allMonthsSearched: true,
  monthsWithoutBooksView: 0,
};

export const SYNTHETIC_TRUST_ACTIVITY_SEARCH: ActivitySearchResponse = {
  items: trustActivitySearchItems,
  matchCount: trustActivitySearchItems.length,
  capped: false,
  searchedFrom: SEARCH_FROM,
  searchedTo: SEARCH_TO,
  allMonthsSearched: true,
  monthsWithoutBooksView: 0,
};

export function emptyActivitySearch(): ActivitySearchResponse {
  return {
    items: [],
    matchCount: 0,
    capped: false,
    allMonthsSearched: true,
    monthsWithoutBooksView: 0,
  };
}

function searchableActivityText(item: ActivityItem): string[] {
  const values = [
    item.description,
    item.amount,
    item.fee,
    item.net,
    item.refId,
    item.moov?.kind,
    item.moov?.property,
    item.moov?.lease,
  ];
  return values.filter((value): value is string => value !== undefined);
}

function activitySearchMatches(item: ActivityItem, query: ActivitySearchQuery): boolean {
  if (query.source !== undefined && query.source !== "all" && item.source !== query.source) {
    return false;
  }
  if (query.state !== undefined && item.state !== query.state) return false;
  const q = query.q.trim().toLowerCase();
  if (q.length === 0) return true;
  return searchableActivityText(item).some((value) => value.toLowerCase().includes(q));
}

export function syntheticActivitySearch(
  company: string,
  query: ActivitySearchQuery,
): ActivitySearchResponse {
  if (company === TRUST_COMPANY_SLUG) {
    const items = SYNTHETIC_TRUST_ACTIVITY_SEARCH.items.filter((item) =>
      activitySearchMatches(item, query),
    );
    return { ...SYNTHETIC_TRUST_ACTIVITY_SEARCH, items, matchCount: items.length };
  }
  if (company === RENTAL_COMPANY_SLUG) {
    const items = SYNTHETIC_ACTIVITY_SEARCH.items.filter((item) =>
      activitySearchMatches(item, query),
    );
    return { ...SYNTHETIC_ACTIVITY_SEARCH, items, matchCount: items.length };
  }
  return emptyActivitySearch();
}

export const SYNTHETIC_LEDGER_SEARCH: LedgerSearchResponse = {
  company: RENTAL_COMPANY_SLUG,
  items: [
    { period: LEDGER_OPEN_PERIOD, state: "open", entry: rentalOpenEntry },
    { period: LEDGER_CLOSED_PERIOD, state: "closed", entry: rentalClosedEntry },
  ],
  matchCount: 2,
  capped: false,
  searchedFrom: LEDGER_CLOSED_PERIOD,
  searchedTo: LEDGER_OPEN_PERIOD,
  monthsSearched: 2,
  allMonthsSearched: true,
  monthsUnreadable: 0,
};

export const SYNTHETIC_TRUST_LEDGER_SEARCH: LedgerSearchResponse = {
  company: TRUST_COMPANY_SLUG,
  items: [{ period: LEDGER_OPEN_PERIOD, state: "open", entry: trustDepositEntry }],
  matchCount: 1,
  capped: false,
  searchedFrom: LEDGER_OPEN_PERIOD,
  searchedTo: LEDGER_OPEN_PERIOD,
  monthsSearched: 1,
  allMonthsSearched: true,
  monthsUnreadable: 0,
};

export function emptyLedgerSearch(company: string): LedgerSearchResponse {
  return {
    company,
    items: [],
    matchCount: 0,
    capped: false,
    monthsSearched: 0,
    allMonthsSearched: true,
    monthsUnreadable: 0,
  };
}

function searchableLedgerText(entry: LedgerEntry): string[] {
  const values = [
    entry.payee,
    entry.narration,
    entry.location,
    entry.ids.lease,
    entry.ids.plaidId,
    entry.ids.moovId,
    entry.ids.pairedPlaidId,
    entry.view.headline,
    entry.view.subline,
    entry.view.cashDelta,
  ];
  for (const posting of entry.postings) {
    values.push(posting.account, posting.amount, posting.location);
  }
  return values.filter((value): value is string => value !== undefined);
}

function ledgerSearchEntryMatches(entry: LedgerEntry, query: LedgerSearchQuery): boolean {
  if (query.account !== undefined) {
    if (!entry.postings.some((posting) => posting.account === query.account)) return false;
  }
  if (query.accountPrefix !== undefined) {
    const prefix = query.accountPrefix;
    const boundary = `${prefix}:`;
    const hit = entry.postings.some(
      (posting) => posting.account === prefix || posting.account.startsWith(boundary),
    );
    if (!hit) return false;
  }
  if (query.location !== undefined && entry.location !== query.location) return false;
  if (query.lease !== undefined && entry.ids.lease !== query.lease) return false;
  if (query.source !== undefined && entry.source !== query.source) return false;
  const q = query.q?.trim().toLowerCase() ?? "";
  if (q.length === 0) return true;
  return searchableLedgerText(entry).some((value) => value.toLowerCase().includes(q));
}

export function syntheticLedgerSearch(
  company: string,
  query: LedgerSearchQuery,
): LedgerSearchResponse {
  const base =
    company === TRUST_COMPANY_SLUG
      ? SYNTHETIC_TRUST_LEDGER_SEARCH
      : company === RENTAL_COMPANY_SLUG
        ? SYNTHETIC_LEDGER_SEARCH
        : null;
  if (base === null) return emptyLedgerSearch(company);
  const items = base.items.filter((hit) => ledgerSearchEntryMatches(hit.entry, query));
  return { ...base, items, matchCount: items.length };
}

const MONTHS_GENERATED_AT = "2026-10-24T12:00:00.000Z";

export const SYNTHETIC_MONTHS: AdminMonthsResponse = {
  company: RENTAL_COMPANY_SLUG,
  generatedAt: MONTHS_GENERATED_AT,
  lastBookingRun: {
    startedAt: "2026-10-24T11:55:00.000Z",
    completedAt: "2026-10-24T12:00:00.000Z",
    outcome: "succeeded",
  },
  months: [
    {
      period: "2026-10",
      status: "needs_you",
      reconciled: "not_yet",
      accountsChecked: 0,
      booksView: "loaded",
      booksAsOf: "2026-10-24T12:00:00.000Z",
      blockers: [
        { key: "cards_to_review", count: 2, target: "to_review" },
        { key: "bank_lines_waiting", count: 1, target: "none" },
      ],
    },
    {
      period: "2026-09",
      status: "waiting",
      reconciled: "not_yet",
      accountsChecked: 2,
      booksView: "loaded",
      booksAsOf: "2026-10-02T12:00:00.000Z",
      blockers: [{ key: "statements_saving", count: 1, target: "none" }],
    },
    {
      period: "2026-08",
      status: "closed",
      closedAt: "2026-09-03T12:00:00.000Z",
      reconciled: "yes",
      accountsChecked: 2,
      booksView: "loaded",
      booksAsOf: "2026-09-03T12:00:00.000Z",
      blockers: [],
    },
    {
      period: "2026-07",
      status: "closed_check_failed",
      closedAt: "2026-08-03T12:00:00.000Z",
      reconciled: "no",
      accountsChecked: 2,
      booksView: "loaded",
      booksAsOf: "2026-08-03T12:00:00.000Z",
      blockers: [{ key: "check_failed_after_close", count: 1, target: "problems" }],
    },
    {
      period: "2026-06",
      status: "reopened",
      reconciled: "not_yet",
      accountsChecked: 0,
      booksView: "not_loaded",
      blockers: [{ key: "reclose_needed", count: 1, target: "to_review" }],
    },
    {
      period: "2026-05",
      status: "open",
      reconciled: "not_yet",
      accountsChecked: 0,
      booksView: "loaded",
      booksAsOf: "2026-06-02T12:00:00.000Z",
      blockers: [],
    },
  ],
  undatedBlockers: [{ key: "cards_to_review", count: 1, target: "to_review" }],
};

export function emptyMonths(company: string): AdminMonthsResponse {
  return {
    company,
    generatedAt: new Date(0).toISOString(),
    lastBookingRun: null,
    months: [],
    undatedBlockers: [],
  };
}

export function syntheticMonths(company: string): AdminMonthsResponse {
  return { ...SYNTHETIC_MONTHS, company };
}

/** Active companies, list order. Slug text is not consulted. */
export function companiesForPicker(companies: readonly Company[]): Company[] {
  const selectable: Company[] = [];
  for (const company of companies) {
    if (company.isActive) selectable.push(company);
  }
  return selectable;
}

export const SYNTHETIC_DEMO_COMPANIES: Company[] = [
  {
    slug: RENTAL_COMPANY_SLUG,
    displayName: "123 Test Street Rentals",
    companyType: "external",
    basis: "cash",
    featurePacks: ["core"],
    hubLink: null,
    isActive: true,
  },
  {
    slug: TRUST_COMPANY_SLUG,
    displayName: "123 Test Street Trust",
    companyType: "external",
    basis: "cash",
    featurePacks: ["core"],
    hubLink: null,
    isActive: true,
  },
];

export const SYNTHETIC_DEMO_GROUPS: DashboardGroup[] = [
  {
    id: "grp-syn-0001",
    name: "Test street books",
    memberSlugs: [RENTAL_COMPANY_SLUG, TRUST_COMPANY_SLUG],
    isDefault: true,
    createdAt: "2026-10-01T00:00:00.000Z",
    updatedAt: "2026-10-01T00:00:00.000Z",
  },
];

const MOOV_BODIES = [
  "no_rule",
  "ambiguous_rules",
  "period_locked",
  "property_not_configured",
  "breakdown_deposit_not_in_trust_wallet",
  "auto_commit_off",
  "account_not_in_chart",
] as const;

export const SYNTHETIC_QUEUE_ITEMS: QueueItem[] = Array.from({ length: 31 }, (_, index) => {
  const number = index + 1;
  const company = index % 2 === 0 ? RENTAL_COMPANY_SLUG : TRUST_COMPANY_SLUG;
  const body = MOOV_BODIES[index % MOOV_BODIES.length] ?? "no_rule";
  return {
    id: number,
    company,
    reason: "close-blocked",
    status: "pending",
    createdAt: `2026-09-01T12:00:${String(index).padStart(2, "0")}.000Z`,
    txnDate: "2026-09-15",
    amount: money(10000n + BigInt(index) * 1234n),
    descriptor: index % 5 === 0 ? "moov payout" : "moov wallet",
    subject: `${MOOV_CARD_SUBJECT_PREFIX}00000000000000${String(number).padStart(2, "0")}`,
    body,
    makeRule: false,
  };
});

export const SYNTHETIC_DEMO_HEALTH: { companies: CompanyHealth[] } = {
  companies: [
    {
      company: RENTAL_COMPANY_SLUG,
      plaidItems: [
        {
          plaidItemId: "item-syn-0001",
          institution: "Synthetic Bank",
          status: "healthy",
          lastSyncedAt: "2026-10-25T11:00:00.000Z",
        },
      ],
      lastRuns: [
        {
          runType: "plaid_sync",
          status: "succeeded",
          startedAt: "2026-10-25T11:00:00.000Z",
          completedAt: "2026-10-25T11:01:00.000Z",
          txnsProcessed: 2,
          queueAdded: 0,
        },
        {
          runType: "processing",
          status: "succeeded",
          startedAt: "2026-10-25T11:05:00.000Z",
          completedAt: "2026-10-25T11:06:00.000Z",
          txnsProcessed: 4,
          queueAdded: 1,
        },
      ],
    },
    {
      company: TRUST_COMPANY_SLUG,
      plaidItems: [
        {
          plaidItemId: "item-syn-0002",
          institution: "Synthetic Bank",
          status: "healthy",
          lastSyncedAt: "2026-10-25T11:00:00.000Z",
        },
      ],
      lastRuns: [
        {
          runType: "processing",
          status: "succeeded",
          startedAt: "2026-10-25T11:05:00.000Z",
          completedAt: "2026-10-25T11:06:00.000Z",
          txnsProcessed: 1,
          queueAdded: 0,
        },
      ],
    },
  ],
};

export function syntheticChartSnapshot(company: string): ChartSnapshot {
  const rental = company !== TRUST_COMPANY_SLUG;
  const accounts = rental
    ? [
        "Assets:Wallet:Operating",
        "Income:Rent",
        "Income:LateFee",
        "Expenses:ProcessorFee",
        "Expenses:Rent",
        "Equity:Opening",
      ]
    : ["Assets:Trust:Cash", "Liabilities:SecurityDeposits"];
  return {
    company,
    present: true,
    pushedAt: LEDGER_COMMIT_AT,
    accounts,
    locations: [PROPERTY],
    selectableAccounts: accounts,
    selectableLocations: [PROPERTY],
  };
}

export function syntheticCheckNow(company: string): CheckNowStatus {
  return {
    company,
    cooldownSeconds: 60,
    nextAllowedAt: null,
    press: {
      pressId: "press-syn-0001",
      requestedAt: "2026-10-25T11:00:00.000Z",
      completedAt: "2026-10-25T11:01:00.000Z",
      status: "done",
      reads: {
        moov: {
          state: "read",
          walletsRead: 1,
          walletsSkipped: 0,
          newMovements: 1,
          refreshedMovements: 0,
          mayHaveMore: false,
        },
        plaid: {
          state: "read",
          itemsRead: 1,
          itemsFailed: 0,
          itemsNeedReconnect: 0,
          newOrChangedLines: 1,
        },
      },
      sync: {
        state: "started",
        startedAt: "2026-10-25T11:00:30.000Z",
        runUrl: null,
      },
      outcome: {
        state: "booked",
        newlyBooked: 1,
        newForReview: 2,
        errorCode: null,
      },
    },
  };
}

export function syntheticDemoBalances(company: string): BalancesSnapshot {
  if (company === TRUST_COMPANY_SLUG) {
    return {
      company,
      asOf: "2026-10-25",
      accounts: [
        {
          name: "Trust cash",
          type: "depository",
          mask: "0000",
          currency: "USD",
          currentBalance: money(DEPOSIT_CENTS),
          asOf: "2026-10-25",
        },
      ],
    };
  }
  return {
    company,
    asOf: "2026-10-25",
    accounts: [
      {
        name: "Operating wallet",
        type: "depository",
        mask: "0001",
        currency: "USD",
        currentBalance: money(walletAfterSeptember + TENANT_NET_CENTS),
        asOf: "2026-10-25",
      },
    ],
  };
}

/** Whole-app stubs. Always include the strings those tests search for. */
export function stubActivityPage(company: string): ActivityPage {
  void company;
  return {
    items: [
      {
        id: "act-stub-0001",
        source: "bank",
        date: "2026-06-03",
        amount: "100.00",
        description: "SYNTHETIC CLIENT ONE",
        state: "processed",
        refId: "0000000000000001",
      },
    ],
    page: 1,
    pageSize: 50,
    total: 1,
    hasMore: false,
    sources: {
      bank: { lastSyncedAt: "2026-06-30T00:00:00.000Z" },
      moov: { walletConfigured: false },
      ledger: { available: false },
    },
  };
}

export function stubLedgerPage(company: string, period: string): LedgerEntriesPage {
  const header = headerFor(company, period, "closed", 0, [
    { period, state: "closed", entryCount: 1 },
  ]);
  return pageFor(header, [
    {
      id: "stub-ledger-0001",
      date: "2026-06-05",
      flag: "*",
      payee: "SYNTHETIC LANDLORD LLC",
      narration: "Office rent",
      source: "rule",
      location: "hq",
      ids: {},
      postings: [
        { account: "Expenses:Rent", amount: "100.00", location: "hq" },
        { account: "Assets:Cash", amount: "-100.00", location: "hq" },
      ],
      view: {
        kind: "expense",
        headline: "Office rent",
        lines: [{ label: "Office rent", amount: "-100.00" }],
      },
    },
  ]);
}

export function stubLedgerAccounts(company: string, period: string): LedgerAccountsResponse {
  const accounts = [
    accountRow("Assets:Cash", "Assets", true, 0n, -10000n, "2026-01-01"),
    accountRow("Expenses:Rent", "Expenses", false, 0n, 10000n, "2026-01-01"),
  ];
  return accountsResponse(headerFor(company, period, "closed", 0, []), accounts);
}

export function stubLedgerStatement(
  company: string,
  report: LedgerStatementReport,
  period: string,
): LedgerStatement {
  return statementEnvelope(company, report, period, "closed", "2026-06-30", {
    currency: "USD",
    periodStart: "2026-06-01",
    periodEnd: "2026-06-30",
    sections: [],
    netIncome: "0.00",
    priorYearNetIncome: null,
    note: "Office rent",
  });
}
